import { useMemo, useState } from "react";
import { chartAccentTint } from "@/components/charts/chartPalette";
import { Link, useNavigate } from "react-router-dom";
import { CalendarCheck, MessageCircle, Paperclip } from "lucide-react";

import { SettingsSectionLabel } from "@/components/settings/SettingsSectionLabel";
import { PMEmptyState } from "@/domains/pm/components/shared";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMyOpenAssignedIssues } from "@/domains/pm/hooks/useMyOpenAssignedIssues";
import { isIssueOverdue } from "@/domains/pm/utils/dueDate";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { usePermissionDenied } from "@/hooks/usePermissionDenied";
import { useProjectFeatureAccess } from "@/hooks/useProjectFeatureAccess";
import { useBuildingScope } from "@/hooks/buildings/useBuildingScope";
import { FEATURE_FLAGS, useFeatureFlag } from "@/lib/posthog";
import { fill } from "@/lib/i18n/fill";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { Language } from "@/lib/i18n/types";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

interface MyTasksWidgetProps {
  buildingId: number;
  /** The active project (`building_projects.id`), or null at "All projects"
   * scope — the widget then covers the whole building. */
  projectId: number | null;
}

const WINDOW_STORAGE_KEY = "franklin:my-tasks-window:v1";
const WINDOW_OPTIONS = [7, 14, 30] as const;
type WindowDays = (typeof WINDOW_OPTIONS)[number];
const DEFAULT_WINDOW: WindowDays = 14;

function loadWindow(): WindowDays {
  try {
    const raw = window.localStorage.getItem(WINDOW_STORAGE_KEY);
    const parsed = raw ? Number(raw) : NaN;
    return (WINDOW_OPTIONS as readonly number[]).includes(parsed)
      ? (parsed as WindowDays)
      : DEFAULT_WINDOW;
  } catch {
    return DEFAULT_WINDOW;
  }
}

/** "6 Oct 2025" — day, short month, year, matching the rest of the app. The
 *  locale is explicit: the browser default would render month-first for en-US
 *  users. */
function formatDueDate(dateStr: string, language: Language): string {
  return new Date(dateStr).toLocaleDateString(language === "et" ? "et-EE" : "en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Not overdue, but due within this many days: a pill in the charts' steel blue. */
const DUE_SOON_DAYS = 3;

function isDueSoon(dueDate: string | null, overdue: boolean): boolean {
  if (!dueDate || overdue) return false;
  const soon = new Date();
  soon.setDate(soon.getDate() + DUE_SOON_DAYS);
  soon.setHours(23, 59, 59, 999);
  return new Date(dueDate) <= soon;
}

/** A quiet icon + count — the same Paperclip / MessageCircle the Issues page uses. */
function CountIcon({
  icon: Icon,
  count,
  label,
}: {
  icon: typeof Paperclip;
  count: number;
  label: string;
}) {
  return (
    <span
      className="inline-flex shrink-0 items-center gap-0.5 text-[11px] tabular-nums text-muted-foreground"
      title={label}
      aria-label={label}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {count}
    </span>
  );
}

/** One task line: muted #id, the title (truncated, full text on hover), icons
 *  for linked documents and comments when it has any, and the due date
 *  right-aligned — an overdue date as a small red pill, one due in the next
 *  few days as a blue pill, everything else as plain grey text. */
function TaskRow({
  issueNumber,
  title,
  dueDate,
  overdue,
  documentsCount,
  commentsCount,
  onSelect,
}: {
  issueNumber: number;
  title: string;
  dueDate: string | null;
  overdue: boolean;
  documentsCount: number;
  commentsCount: number;
  onSelect: () => void;
}) {
  const { t, language } = useLanguage();
  const tw = t.buildings.projectsDashboard.myTasks;
  return (
    <button
      type="button"
      onClick={onSelect}
      className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-2 rounded-md px-2 py-2.5 text-left text-sm transition-colors hover:bg-[#efeff1]"
    >
      <span className="shrink-0 text-xs text-muted-foreground">#{issueNumber}</span>
      <span className="min-w-0 flex-1 truncate" title={title}>
        {title}
      </span>
      {documentsCount > 0 && (
        <CountIcon
          icon={Paperclip}
          count={documentsCount}
          label={fill(tw.documentsCount, { count: String(documentsCount) })}
        />
      )}
      {commentsCount > 0 && (
        <CountIcon
          icon={MessageCircle}
          count={commentsCount}
          label={fill(tw.commentsCount, { count: String(commentsCount) })}
        />
      )}
      {dueDate &&
        (overdue ? (
          <span className="shrink-0 rounded-full bg-[#fcebeb] px-2 py-0.5 text-[11px] font-medium text-[#a32d2d]">
            {formatDueDate(dueDate, language)}
          </span>
        ) : isDueSoon(dueDate, overdue) ? (
          <span
            className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium text-neutral-800"
            style={{ backgroundColor: chartAccentTint("steel", 0.18) }}
          >
            {formatDueDate(dueDate, language)}
          </span>
        ) : (
          <span className="shrink-0 text-[11px] text-muted-foreground">
            {formatDueDate(dueDate, language)}
          </span>
        ))}
    </button>
  );
}

/** The "Due within <window>" chip on the label row — a quiet grey pill, not a
 *  bordered form control. */
function DueWithinChip({
  windowDays,
  onChange,
  labels,
  dueWithinLabel,
}: {
  windowDays: WindowDays;
  onChange: (value: string) => void;
  labels: Record<WindowDays, string>;
  dueWithinLabel: string;
}) {
  return (
    <Select value={String(windowDays)} onValueChange={onChange}>
      <SelectTrigger
        {...pageInfoTarget("dashboard.myTasksWindow")}
        className="h-7 w-auto gap-1.5 rounded-full border-0 bg-[#f0f0f1] px-3 text-xs font-medium text-muted-foreground"
      >
        {/* The trigger's `[&>span]:line-clamp-1` forces `display:-webkit-box` on
            this span, which drops flex `gap` — hence the explicit non-breaking
            space rather than `gap-1`, which rendered "Due within1 week". */}
        <span>
          {`${dueWithinLabel}\u00A0`}
          <SelectValue />
        </span>
      </SelectTrigger>
      <SelectContent>
        {WINDOW_OPTIONS.map((days) => (
          <SelectItem key={days} value={String(days)} className="text-xs">
            {labels[days]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Project-dashboard "My tasks": how many active issues are assigned to the
 * current user in this project, and which of them are due within a
 * user-selectable window (overdue ones included, on top). Rows deep-link into
 * My Issues (`?selected=`); hidden entirely when the PM module is off or the
 * user lacks the PM tool permission.
 *
 * Deliberately NOT a card: the label row (count badge + the "Due within"
 * chip) sits over a plain hairline-separated list on the page, so the
 * dashboard's second row does not repeat the first row's boxes. Each row shows
 * paperclip / comment icons when the issue has linked documents or comments.
 * The list scrolls past the same height cap the cards above use, and "View all
 * my tasks" closes it.
 */
export function MyTasksWidget({ buildingId, projectId }: MyTasksWidgetProps) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const perms = usePermissions();
  const pmModuleEnabled = useFeatureFlag(FEATURE_FLAGS.PM_MODULE_ENABLED, true);
  const { userProfile } = useAuth();

  const [windowDays, setWindowDays] = useState<WindowDays>(loadWindow);

  // Opening My Issues is refused, not attempted, when the member cannot see
  // issues in this project — the same answer as the dimmed sidebar item and
  // ProjectPermissionRouteGuard, so the link never lands on a blocked page.
  const { projectOptions, optionsLoaded } = useBuildingScope(String(buildingId));
  const issuesAccess = useProjectFeatureAccess({
    selectedProjectId: projectId,
    projectOptions,
    optionsLoaded,
  });
  const denyPermission = usePermissionDenied();
  const canOpenIssues = issuesAccess.allowed("issues");
  const refuseIssues = () => denyPermission("viewIssues", projectId);

  // The early-return below hides the widget for users without PM access, but
  // hooks run first — gate the query itself too so no PM fetch ever fires
  // for them (an undefined user id disables it).
  const canUsePM = pmModuleEnabled && perms.canViewPMTool;
  const { data: issues = [], isLoading } = useMyOpenAssignedIssues(
    buildingId,
    projectId,
    canUsePM ? userProfile?.id : undefined
  );

  // Due within the window, overdue included — sorted by due date ascending
  // (repository order), which naturally puts overdue work first.
  const upcoming = useMemo(() => {
    const windowEnd = new Date();
    windowEnd.setDate(windowEnd.getDate() + windowDays);
    windowEnd.setHours(23, 59, 59, 999);
    return issues.filter((issue) => issue.due_date && new Date(issue.due_date) <= windowEnd);
  }, [issues, windowDays]);

  if (!pmModuleEnabled || !perms.canViewPMTool) return null;

  const handleWindowChange = (value: string) => {
    const parsed = Number(value) as WindowDays;
    setWindowDays(parsed);
    try {
      window.localStorage.setItem(WINDOW_STORAGE_KEY, String(parsed));
    } catch {
      // Persistence is a nicety; ignore quota/privacy-mode failures.
    }
  };

  const tw = t.buildings.projectsDashboard.myTasks;
  const windowLabels: Record<WindowDays, string> = {
    7: tw.window.oneWeek,
    14: tw.window.twoWeeks,
    30: tw.window.oneMonth,
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2">
          <SettingsSectionLabel>{tw.title}</SettingsSectionLabel>
          {issues.length > 0 && (
            <span className="flex-shrink-0 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary-foreground">
              {issues.length}
            </span>
          )}
        </span>
        <DueWithinChip
          windowDays={windowDays}
          onChange={handleWindowChange}
          labels={windowLabels}
          dueWithinLabel={tw.dueWithin}
        />
      </div>

      {upcoming.length === 0 && !isLoading ? (
        <PMEmptyState
          icon={CalendarCheck}
          title={tw.empty}
          className="flex-1 px-4 py-8"
          iconColor={chartAccentTint("teal", 0.7)}
        />
      ) : (
        <ul className="mt-3 max-h-[420px] divide-y divide-neutral-200 overflow-y-auto border-y border-neutral-200 px-2">
          {upcoming.map((issue) => (
            <li key={issue.id}>
              <TaskRow
                issueNumber={issue.issue_number}
                title={issue.title}
                dueDate={issue.due_date}
                overdue={isIssueOverdue(issue)}
                documentsCount={issue.documents_count}
                commentsCount={issue.comments_count}
                onSelect={() =>
                  canOpenIssues
                    ? navigate(`/buildings/${buildingId}/my-issues?selected=${issue.id}`)
                    : refuseIssues()
                }
              />
            </li>
          ))}
        </ul>
      )}
      {/* A flex line of text-xs, the same footer the Brief list beside it
          closes with, so the two end on the same line. */}
      <div className="flex items-center pt-2.5">
        {canOpenIssues ? (
          <Link
            to={`/buildings/${buildingId}/my-issues`}
            className="text-xs font-medium text-primary hover:underline"
          >
            {tw.viewAll} →
          </Link>
        ) : (
          <button
            type="button"
            onClick={refuseIssues}
            className="text-xs font-medium text-primary hover:underline"
          >
            {tw.viewAll} →
          </button>
        )}
      </div>
    </div>
  );
}
